import type { AxiosError } from 'axios';
import axios from 'axios';

import { Capacitor } from '@capacitor/core';
import type { DeviceInfo } from '@capacitor/device';
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { environment } from '../../environment/environment';
import { BASE_ENDPOINT } from '../../helpers/utils.helper';
import type { ErrorMessage } from '../../models/error';
import type { AsyncThunkConfig } from '../../models/slice';
import { RaygunErrorHandlerService } from '../../services/raygun.service';

const { logError } = RaygunErrorHandlerService();

export const showStores = async () => {
  const platform = Capacitor.getPlatform() as DeviceInfo['platform'];

  if (platform === 'ios') {
    window.open('https://apps.apple.com/us/app/trycycle/id1367938317', '_system');
  } else {
    const handle = window.open('market://details?id=com.trycycledata', '_system');
    // if failed to open on a device without the Play Store, open on browser
    if (!handle) {
      window.open('https://play.google.com/store/apps/details?id=com.trycycledata', '_system');
    }
  }
};

type AppDetail = {
  app_version: string;
  build_number: string;
  operating_system: string | null;
  operating_system_version: string | null;
  phone_model: string | null;
  connection_type: string | null;
};

const APP_STATUS = {
  APP_ERROR: 'error',
  APP_EXPIRED: 'expired',
  APP_LATEST: 'latest',
  APP_START: 'start',
  APP_UPDATE_AVAILABLE: 'update_available',
} as const;

export type APP_STATUS = (typeof APP_STATUS)[keyof typeof APP_STATUS];

export const checkAppVersion = createAsyncThunk<APP_STATUS, undefined, AsyncThunkConfig>(
  'appUpdate/checkAppVersion',
  async (_, thunkAPI) => {
    try {
      const appData: AppDetail = {
        app_version: environment.versionNumber,
        build_number: environment.buildNumber,
        operating_system: null,
        operating_system_version: null,
        phone_model: null,
        connection_type: null,
      };
      if (Capacitor.isPluginAvailable('Device')) {
        const { Device } = await import('@capacitor/device');
        const info = await Device.getInfo();
        appData.operating_system = info.platform;
        appData.operating_system_version = info.osVersion;
        appData.phone_model = info.manufacturer + ', Model: ' + info.model;
      }
      if (Capacitor.isPluginAvailable('Network')) {
        const { Network } = await import('@capacitor/network');
        const network = await Network.getStatus();
        appData.connection_type = network.connectionType;
      }

      const endpoint = 'v3_upgrade';
      const [resp1, resp2] = await Promise.allSettled([
        axios.post(endpoint, appData, { baseURL: BASE_ENDPOINT.CA }) as unknown as APP_STATUS,
        axios.post(endpoint, appData, { baseURL: BASE_ENDPOINT.US }) as unknown as APP_STATUS,
      ]);
      if (resp1.status === 'fulfilled') {
        return resp1.value;
      } else if (resp2.status === 'fulfilled') {
        return resp2.value;
      } else if (resp1.status === 'rejected') {
        throw resp1.reason;
      } else if (resp2.status === 'rejected') {
        throw resp2.reason;
      } else {
        throw new Error('Unexpected Promise.allSettled status for both CA and US endpoints');
      }
    } catch (e) {
      logError(e, ['appUpdateSlice', 'checkAppVersion']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const checkHasNotificationToken = createAsyncThunk<boolean | undefined, undefined, AsyncThunkConfig>(
  'appUpdate/checkHasNotificationToken',
  async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().login.authResult.token;
      if (!token) return;

      if (Capacitor.isPluginAvailable('Device')) {
        const { Device } = await import('@capacitor/device');
        const { identifier: device_native_id } = await Device.getId();
        const response = (await axios.post('v4_has_notification_token', { device_native_id })) as boolean;
        return response;
      }
    } catch (e) {
      logError(e, ['appUpdateSlice', 'checkHasNotificationToken']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

type AppUpdateSliceType = {
  showExpired: boolean;
  showUpdateAlert: boolean;
  showLocationServicesDisabledModal: boolean;
};

const initialState: AppUpdateSliceType = {
  showExpired: false,
  showUpdateAlert: false,
  showLocationServicesDisabledModal: false,
};

export const appUpdateSlice = createSlice({
  name: 'appUpdate',
  initialState,
  reducers: {
    clearAppUpdateState: () => initialState,
    setShowLocationServicesDisabledModal: (state, action: PayloadAction<boolean>) => {
      state.showLocationServicesDisabledModal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(checkAppVersion.fulfilled, (state, action) => {
      state.showExpired = action.payload === APP_STATUS.APP_EXPIRED;
      state.showUpdateAlert = action.payload === APP_STATUS.APP_UPDATE_AVAILABLE;
    });
  },
});

export const { clearAppUpdateState, setShowLocationServicesDisabledModal } = appUpdateSlice.actions;
